import {
  ButtonsDropdownOption,
  Dropdown,
  dropdownTypes,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import {
  render1stOccurrence,
  renderAcronym,
  renderCount,
  renderDefinition,
  renderLocation,
  renderStatus,
  sortData,
} from "components/Modal/renderersAcronymReportModal";
import Tree from "components/Tree";
import { modalTypes } from "constants/types";
import * as PropTypes from "prop-types";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import { requestPoll } from "slices/app/actions/poller";
import { removeErrorMessage } from "slices/app/errorSlice";
import {
  acronymExport,
  getAcronymReport,
  statusAcronymExport,
} from "slices/workspace/actions/workspace";
import { calculateDynamicHeight, getImageFromType, isDocx } from "utils";
import handleKeyboardClick from "utils/handleKeyboardClick";

export const reportColumns = [
  {
    title: "Acronym",
    name: "name",
    width: "8vw",
  },
  {
    title: "Definition",
    name: "definition",
    width: "12vw",
  },
  {
    title: "Status",
    name: "status",
    width: "8vw",
  },
  {
    title: "Location",
    name: "location",
    width: "14vw",
  },
  {
    title: "1st occurrence",
    name: "paragraph",
    width: "41vw",
  },
  {
    title: "Count",
    name: "occurrences",
    width: "7vw",
    type: "number",
  },
  {
    title: "",
    width: "1vw",
    sortable: false,
    resizable: false,
  },
];

const handleExport = ({
  reportType,
  dispatch,
  folderId,
  documentId,
  documentName,
}) => {
  dispatch(removeErrorMessage);
  dispatch(
    requestPoll(
      acronymExport({
        reportType,
        folderId,
        docId: documentId,
        docRef: documentName,
      }),
      statusAcronymExport,
      handleExportSuccess,
      (error) => handleExportFailure(error, dispatch),
      <>
        Sorry we can&apos;t export acronyms at this time, please try again later
      </>
    )
  );
};

const exportOptions = ({ dispatch, folderId, documentId, documentName }) => {
  const options = [
    <ButtonsDropdownOption
      text="Export To Excel"
      content="Export To Excel"
      onClick={() =>
        handleExport({
          reportType: "excelAcronyms",
          dispatch,
          folderId,
          documentId,
          documentName,
        })
      }
    />,

    <ButtonsDropdownOption
      text="Export To Word"
      content="Export To Word"
      onClick={() =>
        handleExport({
          reportType: "detailedDocsAcronyms",
          dispatch,
          folderId,
          documentId,
          documentName,
        })
      }
    />,
  ];
  if (isDocx(documentName)) {
    options.push(
      <ButtonsDropdownOption
        text="Export To Annotated Doc"
        content="Export To Annotated Doc"
        onClick={() => {
          dispatch(hideModal(modalTypes.ACRONYM_REPORT));
          dispatch(
            showModal(modalTypes.ANNOTATED_ACRONYM_REPORT_SELECTION, {
              folderId,
              documentId,
            })
          );
        }}
      />
    );
  }
  return options;
};

const handleClose = ({ dispatch }) => {
  dispatch(hideModal(modalTypes.ACRONYM_REPORT));
};

const handleEdit = ({ dispatch, navigate }) => {
  dispatch(hideModal(modalTypes.ACRONYM_REPORT));
  navigate("/settings/ignore-lists/acronym");
};

const handleExportSuccess = (job) => {
  if (job?.data?.jobMetaInfo?.file) {
    window.location = job?.data?.jobMetaInfo?.file;
  }
};

const handleExportFailure = (error, dispatch) => {
  dispatch(showErrorModal({ message: error }));
};

const processData = ({
  type,
  newSortColumn,
  newOrder,
  setData,
  sortColumn,
  order,
  columns,
  data,
  prepareResultData,
  tree,
}) => {
  let actualSortColumn = sortColumn;
  if (newSortColumn) {
    actualSortColumn = newSortColumn;
  }
  let actualSortOrder = order;
  if (newOrder) {
    actualSortOrder = newOrder;
  }
  if (!type) {
    type = columns.filter((column) => column.name === actualSortColumn).type;
  }
  if (data?.tree === undefined) {
    return;
  }
  setData({
    tree: {
      id: "0",
      children: [],
    },
  });
  const sortedData = sortData(
    data?.tree?.children,
    actualSortColumn,
    actualSortOrder,
    type
  );
  prepareResultData(sortedData);
  setData({
    tree: {
      id: "0",
      children: sortedData,
    },
  });
  tree.treeRef.current.state.recomputeTree({
    refreshNodes: true,
    useDefaultOpenness: false,
  });
};

const AcronymReportModal = ({ folderId, documentId, documentName }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [report, setReport] = useState(undefined);
  const [columns] = useState(reportColumns);
  const [sortColumn, setSortColumn] = useState("name");
  const [order, setOrder] = useState("ASC");
  const [tree] = useState({});
  const scratchRef = useRef({});

  useLayoutEffect(() => {
    const resizeEventHandler = () => {
      tree.treeRef.current.state.recomputeTree({
        refreshNodes: true,
        useDefaultOpenness: false,
      });
    };
    window.addEventListener("resize", resizeEventHandler);
    return () => {
      window.removeEventListener("resize", resizeEventHandler);
    };
  }, []);

  let index = 1;
  const prepareResultData = (d) => {
    d.forEach((i) => {
      i.id = "" + index;
      index++;
    });
  };

  const onSuccess = (result) => {
    prepareResultData(result.acronyms);
    setReport(result);
    setData({
      tree: {
        id: "0",
        children: result.acronyms,
      },
    });
  };

  const handleSort = (newSortColumn, type) => {
    let newOrder;
    if (sortColumn === newSortColumn) {
      if (order === "ASC") {
        newOrder = "DESC";
      } else {
        newOrder = "ASC";
      }
    } else {
      setSortColumn(newSortColumn);
      newOrder = "ASC";
    }
    setOrder(newOrder);
    processData({
      type,
      newSortColumn,
      newOrder,
      setData,
      sortColumn,
      order,
      columns,
      data,
      prepareResultData,
      tree,
    });
  };

  useEffect(() => {
    dispatch(getAcronymReport(folderId, documentId, onSuccess));
  }, []);

  const createCell = (row, header, column) =>
    [
      () => renderAcronym(row),
      () => renderDefinition(row),
      () => renderStatus(row),
      () => renderLocation(row),
      () => render1stOccurrence(row),
      () => renderCount(row),
      () => "",
    ][column]();

  const renderRow = ({ data: row, _isOpen, style, _toggle }) => {
    return (
      <div style={style}>
        {row.isRoot === false ? (
          <>
            <div className="tree_body_row">
              {columns.map((header, column) => {
                const columnStyle = {
                  width: header.width,
                  minWidth: header.width,
                  maxWidth: header.width,
                  flex: 1,
                };
                return (
                  <div className="tree_cell tree_body_cell" style={columnStyle}>
                    {createCell(row, header, column)}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const itemSize = (itemIndex) => {
    if (!itemIndex) {
      return 0;
    }
    if (tree?.treeRef?.current) {
      const columnWidths = [
        {
          w:
            (tree?.treeRef?.current?.props.width * parseInt(columns[0].width)) /
            100,
          f: data.tree.children[itemIndex - 1].name,
        },
        {
          w:
            (tree?.treeRef?.current?.props.width * parseInt(columns[1].width)) /
            100,
          f: data.tree.children[itemIndex - 1].definition,
        },
        {
          w:
            (tree?.treeRef?.current?.props.width * parseInt(columns[2].width)) /
            100,
          f: data.tree.children[itemIndex - 1].status,
        },
        {
          w:
            (tree?.treeRef?.current?.props.width * parseInt(columns[4].width)) /
            100,
          f: data.tree.children[itemIndex - 1].paragraph,
        },
      ];

      return Math.max.apply(
        Math,
        columnWidths.map((d) =>
          calculateDynamicHeight(d.f, d.w + "px", scratchRef, 35)
        )
      );
    }
    return 0;
  };
  const barWidth = `${parseInt((100 * report?.validCount) / report?.uniqueCount)}%`;

  return (
    <Modal
      data-testid="acronym-report-modal"
      className="fade-in acronym_report_modal"
      show
      onDismiss={() => handleClose({ dispatch })}
      centered
    >
      <Modal.Header>Acronym Report</Modal.Header>
      <Modal.Body style={{ whiteSpace: "break-spaces" }}>
        <div id="scratch" className="scratch" ref={scratchRef} />
        <div className="title_container flexed">
          <img
            className="title_container_icon"
            src={getImageFromType(report?.iconClass)}
            alt="document icon"
          />

          <span className="title_container_title">{documentName}</span>
          <span
            /* eslint-disable-next-line react/no-unknown-property */
            dataTestId="edit-button"
            /* eslint-disable-next-line react/no-unknown-property */
            variant={"l2"}
            onClick={() => handleEdit({ dispatch, navigate })}
            onKeyDown={handleKeyboardClick(() => handleEdit({ dispatch, navigate }))}
            role="button"
            tabIndex={0}
            className="export_link"
          >
            Edit Acronym Ignore List
          </span>
          <div className="dropdown_container">
            <Dropdown
              type={dropdownTypes.buttons}
              propsObject={{
                text: "Export",
                options: exportOptions({
                  dispatch,
                  folderId,
                  documentId,
                  documentName,
                }),
                buttonVariant: "l2",
              }}
            />
          </div>
        </div>
        <div className="stats_container">
          <div className="stats">
            <div className="line">Total likely acronyms</div>
            <div className="line" />
            <div className="line">{report?.uniqueCount} unique found</div>
          </div>
          <div className="stats">
            <div className="line">Well-defined acronyms</div>
            <div className="line">
              <div className="acronym_indicator">
                <div
                  className="acronym_indicator_bar"
                  style={{ width: barWidth }}
                />
              </div>
            </div>
            <div className="line">
              <span className="blue">{report?.validCount}</span> out of{" "}
              {report?.uniqueCount} Acronyms
            </div>
          </div>
          <div className="stats">
            <div className="line">
              Acronyms with No Definition{" "}
              <span className="right red">{report?.undefinedCount}</span>
            </div>
            <div className="line">
              with Conflicting Definitions{" "}
              <span className="right red">
                {report?.multipleDefinitionsCount}
              </span>
            </div>
            <div className="line">
              used before Definition{" "}
              <span className="right brown">
                {report?.usedBeforeDefinitionCount}
              </span>
            </div>
          </div>
        </div>
        <Tree
          data={data}
          renderRow={renderRow}
          columns={columns}
          sortable
          resizable
          sortColumn={sortColumn}
          sortOrder={order}
          onSort={handleSort}
          itemSize={itemSize}
          widthAdjust={10}
          tree={tree}
        />
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="close-button"
          variant={NewButtonVariant.l2}
          onClick={() => handleClose({ dispatch })}
        >
          Close
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AcronymReportModal;

AcronymReportModal.propTypes = {
  folderId: PropTypes.string.isRequired,
  documentId: PropTypes.string.isRequired,
  documentName: PropTypes.string.isRequired,
};
